import { useState, type HTMLAttributes, type ReactNode } from 'react';
import { useId } from 'react-aria';
import { type AnalyticsDataAttributes } from '@hungryroot/segment';
import { Minus, Plus, Heading, TransparentButton } from '@hungryroot/ui-kit';
import styles from './Accordion.module.css';

interface Props {
  title: ReactNode;
  children: ReactNode;
  className?: string;
  headingClassName?: string;
  buttonClassName?: string;
  bodyClassName?: string;
  expanded?: boolean;
  onToggle?: (data: { expanded: boolean }) => void;
  buttonTestId?: string;
  buttonExtraProps?: HTMLAttributes<HTMLButtonElement> &
    AnalyticsDataAttributes;
}

export function Accordion({
  onToggle,
  title,
  children,
  className,
  headingClassName,
  buttonClassName,
  bodyClassName,
  expanded: expandedProp,
  buttonTestId,
  buttonExtraProps,
}: Props) {
  const id = useId();
  const sectionId = useId();
  const [internalExpanded, setInternalExpanded] = useState(false);
  const expanded = expandedProp ?? internalExpanded;
  const Icon = expanded === true ? <Minus /> : <Plus />;

  return (
    <div className={className}>
      <header id={id} className={headingClassName}>
        <Heading variant="H6">
          <TransparentButton
            className={`${styles.row} ${buttonClassName ?? ''}`}
            ariaControls={sectionId}
            ariaExpanded={expanded === true}
            testid={buttonTestId}
            {...buttonExtraProps}
            onClick={() => {
              const next = !expanded;
              if (expandedProp === undefined) {
                setInternalExpanded(next);
              }
              onToggle?.({ expanded: next });
            }}
          >
            {title}
            <span className={styles.icon}>{Icon}</span>
          </TransparentButton>
        </Heading>
      </header>
      {expanded === true && (
        <section
          className={`${styles.body} ${bodyClassName ?? ''}`}
          id={sectionId}
          aria-labelledby={id}
        >
          {children}
        </section>
      )}
    </div>
  );
}
